import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { CreateArtefactVersion, Environments } from "@emisgroup/cohort-builder-kit";
import { useUser } from "../../../../AuthContext";
import { IRootState } from "../../../../reducer/CombineReducer";
import { setNotification } from "../../../../utilities/HttpRequest/Script";
import { ProgressSpinnerSlice } from "../../../ProgressSpinner/Index";
import { handleException } from "../../../../ErrorMessages/helper";
import { VALUE_LIMIT } from "../GetVersions/Constant";
import { initialState } from "./Constant";
import { CreateVersionFormView } from "./CreateVersionForm.view";
import { VersioningSlice } from "./redux";
import { GetVersioning } from "./Scripts";

/**
 * Responsible for rendering the create new version component
 */
export const CreateVersion: React.FC = (): JSX.Element => {

  const dispatch = useDispatch();
  const [formData, setFormData] = useState(initialState);
  const extractState = useSelector((state: IRootState) => state.extractState);
  const selectedExtract = extractState.selectedExtract;
  const extractGuid = selectedExtract?.extractGuid as string;
  const { user } = useUser();
  const { t: translate } = useTranslation();
  const env = process.env.APP_TARGET_ENV as Environments;

  /**
   * Handler for dialog cancel button
   */
  const onCancelDialog = (): void => {
    dispatch(VersioningSlice.actions.showDialog(false));
  };

  /**
   * Handler for creating the new extract version
   */
  const onCreateNewVersion = async(): Promise<void> => {
    const payload = {
      name: formData.name.value.trim()
    };

    if (formData.description.value) {
      payload["description"] = formData.description.value.trim();
    }

    try {
      dispatch(ProgressSpinnerSlice.actions.showProgressBar(translate("LoadingMessage.CreateVersion")));
      const response = await CreateArtefactVersion(payload,extractGuid,env,user.token);
      if (response) {
        dispatch(ProgressSpinnerSlice.actions.hideProgressBar());
        dispatch(VersioningSlice.actions.showDialog(false));
        GetVersioning(dispatch, translate, env, user?.token, extractGuid);
        setNotification(dispatch, "create-version", translate("Notification.CreateVersion"), "success");
      }
    } catch(ex){
      handleException(ex, dispatch, "CreateExtractVersionErrMsg");
      dispatch(VersioningSlice.actions.showDialog(false));
    }
  };

  /**
   * Handler for title and description change
   */
  const handleOnChange = (event: React.SyntheticEvent<any, Event>): void => {
    const fieldName = event.currentTarget.name;
    const fieldValue = event.currentTarget.value;
    let valid = true;
    let errorText = "";
    // Regular expression to allow only alphanumeric along with underscores and dot
    const regexForVersionName = /^[_A-Za-z0-9.]+$/;
    const trimmedValue = fieldValue.trim();
    if(trimmedValue?.length > VALUE_LIMIT) {
      valid = false;
      errorText = translate("Versioning.FormFieldErrors.LengthError");
    }
    if(trimmedValue === "" || (fieldName === "name" && !regexForVersionName.test(trimmedValue))) {
      valid = false;
      errorText = translate("Versioning.FormFieldErrors.NameError");
    }

    setFormData({
      ...formData,
      [fieldName]: {
        value: event.currentTarget.value,
        isValid: valid,
        errorText: errorText
      }
    });
  };

  return (
    <CreateVersionFormView
      onCreate={onCreateNewVersion}
      onCancel={onCancelDialog}
      onChange={handleOnChange}
      formFields={formData}
    />
  );
};
